import { hasQueryStringParameter } from "owa-querystring";
import { getWindowData } from "owa-window-data";

/**
 * PAS (aka.ms/PAS) records each Outlook boot once through a Web Page Replay (WPR) proxy and then
 * replays the saved responses for the measured runs. During replay WPR picks the saved response by
 * request URL only (POST bodies are ignored), so per-session URL values such as the request counter
 * (`n`), correlation vector (`cv`) and correlation id (`cri`) make requests receive responses that
 * belong to other requests.
 *
 * When the page URL has `?pas` (both the recording run and the replay runs), request builders keep
 * those values out of the URL (they are still sent as headers) and add a fingerprint of the request
 * payload instead, so the same logical request gets the same URL when recorded and when replayed.
 */
export const PAS_REPLAY_KEY_PARAM = "pasKey";
export const PAS_REPLAY_OPERATION_PARAM = "pasOp";

let cachedIsPasSession: boolean | undefined;

export function isPasSession(): boolean {
    if (cachedIsPasSession === undefined) {
        // On the data worker the page location only becomes available once the main thread has sent
        // its window data, so don't cache a result computed before that.
        if (!getWindowData()?.location) {
            return false;
        }
        
        cachedIsPasSession = hasQueryStringParameter("pas");
    }
    
    return cachedIsPasSession;
}

/**
 * Returns a short, repeatable fingerprint of a request payload: the same text always gives the same
 * number, and different text almost always gives a different one. It only needs to tell this
 * session's requests apart, so it uses the classic "multiply by 31 and add the next character"
 * string hash (the same formula as Java's String.hashCode) instead of a cryptographic hash, which
 * would add a library to the startup bundle for every user.
 */
export function getPasReplayKey(payload: string): string {
    const range = 2 ** 32;
    let hash = 0;
    for (let i = 0; i < payload.length; i++) {
        // Wrapping around at 2^32 keeps the number small enough to stay exact.
        hash = (hash * 31 + payload.charCodeAt(i)) % range;
    }
    
    return String(hash);
}
